import { Task } from '../types/task.js';

export interface TaskUpdateResult {
    updates: Partial<Task>;
    extraUpdates?: Partial<Task>;
    shouldToggle?: boolean;
}

/**
 * Calculates updates for starting a task timer.
 */
export function calculateStartTaskUpdates(task: Task, now: Date = new Date()): Partial<Task> {
    const baseMs = task.actualDurationMinutes ? task.actualDurationMinutes * 60000 : 0;
    // If we have previous duration, we "resume" by setting the start time back
    // so that (now - start) equals the previous duration + new elapsed time.
    const resumedStart = baseMs ? new Date(now.getTime() - baseMs) : now;

    return {
        status: 'started',
        timerStartedAt: resumedStart,
        progress: task.progress || 0,
        completed: false,
        completedBy: undefined,
        lastCompletedDate: undefined
    };
}

/**
 * Calculates updates for stopping/pausing a task timer.
 */
export function calculateStopTaskUpdates(task: Task, now: Date = new Date()): Partial<Task> {
    if (!task.timerStartedAt) {
        return { status: 'pending', timerStartedAt: null };
    }

    const startedAt = typeof task.timerStartedAt === 'string'
        ? new Date(task.timerStartedAt)
        : task.timerStartedAt;

    const elapsedMs = now.getTime() - startedAt.getTime();
    const rawMinutes = elapsedMs / 60000;
    // Ensure at least 0.01 mins if it was running, to avoid 0
    const elapsedMinutes = Math.max(0.01, Math.round(rawMinutes * 100) / 100);

    return {
        status: 'pending',
        timerStartedAt: null,
        actualDurationMinutes: elapsedMinutes
    };
}

/**
 * Calculates updates for toggling a task's completion status.
 * If the task is currently running (timer active), it stops the timer and saves the duration
 * as part of the completion process.
 */
export function calculateToggleCompletionUpdates(task: Task, now: Date = new Date()): TaskUpdateResult {
    const updates: Partial<Task> = {};
    let extraUpdates: Partial<Task> | undefined;

    // Case 1: Task is already completed -> Mark as incomplete
    if (task.completed) {
        // We don't need any special calculation for un-completing beyond the toggle itself,
        // unless we want to resume the timer (which we usually don't do automatically on un-complete)
        return { shouldToggle: true, updates };
    }

    // Case 2: Task is incomplete -> Mark as completed

    // If timer is running, stop it and save duration
    if (task.timerStartedAt) {
        const stopUpdates = calculateStopTaskUpdates(task, now);

        // We want to save these updates (duration, etc.) separately or merge them?
        // The UI `onToggle` usually handles the toggle boolean itself, but might accept extra updates.
        // In the existing code: `onToggle(id, completionType, extraUpdates)`

        extraUpdates = {
            ...stopUpdates,
            // Ensure we clear timer state
            timerStartedAt: null
        };
    }

    return {
        shouldToggle: true,
        updates,
        extraUpdates
    };
}

/** Window within which a second "complete" on a recurring task is a double-tap, not a new run. */
export const RECURRING_COMPLETION_DEDUPE_MS = 3 * 60 * 1000;

/**
 * True when completing this recurring task NOW would duplicate a completion
 * that just happened. Recurring parents never flip `completed` — completing
 * one snoozes it and inserts a snapshot row — so nothing stops a second tap
 * (list reflow, sync lag, impatience) from minting a second snapshot at the
 * same minute. Seen live: two "Afwasmachine leegmaken" snapshots 19s apart.
 *
 * A genuine second run later the same day (dishwasher runs twice) arrives
 * hours after `lastCompletedDate` and passes untouched.
 */
export function isDuplicateRecurringCompletion(
    task: Task,
    now: Date = new Date(),
    windowMs: number = RECURRING_COMPLETION_DEDUPE_MS,
): boolean {
    if (task.frequency !== 'recurring') return false;
    if (task.completed) return false; // un-completing, not completing
    if (!task.lastCompletedDate) return false;
    const last = task.lastCompletedDate instanceof Date
        ? task.lastCompletedDate.getTime()
        : new Date(task.lastCompletedDate).getTime();
    if (Number.isNaN(last)) return false;
    return now.getTime() - last < windowMs;
}

/**
 * Calculates updates for grabbing (assigning to self) or ungrabbing a task.
 */
export function calculateGrabTaskUpdates(task: Task, userId: string): TaskUpdateResult {
    const assignedTo = task.assignedTo || [];
    const isAssigned = assignedTo.includes(userId);

    const nextAssignedTo = isAssigned
        ? assignedTo.filter(id => id !== userId)
        : [...assignedTo, userId];

    return {
        updates: {
            assignedTo: nextAssignedTo
        }
    };
}

/**
 * Calculates the median duration from a list of tasks.
 */
export function calculateMedianDuration(tasks: Task[]): number | null {
    if (!tasks || tasks.length === 0) return null;

    const durations = tasks
        .map(t => t.actualDurationMinutes)
        .filter((d): d is number => d !== null && d !== undefined && d > 0)
        .sort((a, b) => a - b);

    if (durations.length === 0) return null;

    const mid = Math.floor(durations.length / 2);

    if (durations.length % 2 !== 0) {
        return durations[mid];
    } else {
        return (durations[mid - 1] + durations[mid]) / 2;
    }
}
